import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { Row } from '../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../CommonComponents/OPL/Grid/Column';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { DraftAccountDTO } from '../../AccountOpening.meta';
import { EditableField } from '../../components/EditableField/EditableField';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Grid } from '../../../../CommonComponents/OPL/Grid/Grid';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { SpaceProps } from '../../../../theme/styledProps';
import { InputMask } from '../../../../CommonComponents/OPL/Input/InputMask';
import { ABBREVIATED_STATES_US } from '../../../../CommonConstants';

interface Props extends SpaceProps {
  isReview?: boolean;
  fieldName: 'primaryApplicant' | 'secondApplicant';
  draftAccount: DraftAccountDTO;
}

export const Address: React.FC<Props> = ({ fieldName, isReview, draftAccount, ...rest }) => {
  const formBag = useFormContext();

  const {
    formState: { errors },
  } = formBag;

  const [isEditing, setIsEditing] = useState(!isReview);

  const address1FieldName = `${fieldName}.streetAddressLine1`;
  const address2FieldName = `${fieldName}.streetAddressLine2`;
  const cityFieldName = `${fieldName}.city`;
  const stateFieldName = `${fieldName}.state`;
  const zipFieldName = `${fieldName}.postalCode`;

  const applicant = draftAccount[fieldName];

  if (!isEditing) {
    return (
      <EditableField onEdit={() => setIsEditing(true)}>
        <Text fontWeight={500}>{applicant.streetAddressLine1}</Text>
        {applicant.streetAddressLine2 && (
          <Text fontWeight={500}>{applicant.streetAddressLine2}</Text>
        )}
        <Text fontWeight={500}>
          {applicant.city}, {applicant.state} {applicant.postalCode}
        </Text>
      </EditableField>
    );
  }

  return (
    <Box {...rest}>
      {!isReview && (
        <Text variant="formLabel" mb="4">
          Address
        </Text>
      )}

      <Box>
        <Input
          {...formBag.register(address1FieldName)}
          type="text"
          placeholder="Address Line 1"
          formVariant={get(errors, address1FieldName) ? 'error' : undefined}
          helperText={get(errors, address1FieldName)?.message}
          autoComplete="address-line1"
          aria-label="Address Line 1"
          maxLength={256}
        />
      </Box>

      <Box mt="8">
        <Input
          {...formBag.register(address2FieldName)}
          type="text"
          placeholder="Address Line 2"
          autoComplete="address-line2"
          aria-label="Address Line 2"
          maxLength={256}
        />
      </Box>

      <Grid>
        <Row mt="8">
          <Column width={6}>
            <Input
              {...formBag.register(cityFieldName)}
              type="text"
              placeholder="City"
              formVariant={get(errors, cityFieldName) ? 'error' : undefined}
              helperText={get(errors, cityFieldName)?.message}
              autoComplete="address-level2"
              aria-label="City"
              maxLength={256}
            />
          </Column>

          <Column width={3}>
            <Controller
              control={formBag.control}
              name={stateFieldName}
              render={({ field }) => (
                <Select
                  innerRef={field.ref}
                  value={ABBREVIATED_STATES_US.find(({ value }) => value === field.value)}
                  onChange={opt => {
                    if (opt) {
                      formBag.setValue(stateFieldName, opt.value, { shouldValidate: true });
                    }
                  }}
                  options={ABBREVIATED_STATES_US}
                  placeholder="State"
                  formVariant={get(errors, stateFieldName) ? 'error' : undefined}
                  helperText={get(errors, stateFieldName)?.message}
                  aria-label="State"
                />
              )}
            />
          </Column>

          <Column width={3}>
            <InputMask
              {...formBag.register(zipFieldName)}
              type="text"
              mask="99999"
              placeholder="Zip code"
              formVariant={get(errors, zipFieldName) ? 'error' : undefined}
              helperText={get(errors, zipFieldName)?.message}
              autoComplete="postal-code"
              aria-label="Zip/Postal Code"
            />
          </Column>
        </Row>
      </Grid>
    </Box>
  );
};
